/**
 * KAN-810: page "Лиды по теплоте" (/leads): open leads by temperature (hot,
 * warm, cool) with filters, selection and bulk actions (Excel export, manager,
 * transferred mark). Data and state live in useLeadTemperaturePage.
 * KAN-927: level cold, "Всего: N", the table has its own height.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { FillViewportColumn } from '@/components/common/FillViewportColumn';
import { PaginationBar } from '@/components/common/PaginationBar';
import { LeadFiltersBar } from '@/components/leads/LeadFiltersBar';
import { LeadTemperatureTable } from '@/components/leads/LeadTemperatureTable';
import { LeadBulkBar } from '@/components/leads/LeadBulkBar';
import { LeadAssignManagerDialog, LeadTransferDialog } from '@/components/leads/LeadBulkDialogs';
import { useLeadTemperaturePage } from '@/hooks/useLeadTemperature';
import { extractErrorMessage } from '@/lib/extractErrorMessage';

type OpenDialog = 'none' | 'transfer' | 'manager';

export function LeadTemperaturePage() {
  const { filters, paging, selection, list, bulk, exporter } = useLeadTemperaturePage();
  const [dialog, setDialog] = useState<OpenDialog>('none');
  const closeDialog = () => setDialog('none');
  const ids = selection.ids;
  const data = list.data;

  let content;
  if (list.isLoading) {
    content = (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  } else if (list.isError) {
    content = (
      <p className="py-8 text-center text-sm text-red-700">
        {extractErrorMessage(list.error, 'Не удалось загрузить лидов')}
      </p>
    );
  } else if (!data || data.results.length === 0) {
    content = (
      <p data-testid="leads-empty" className="py-12 text-center text-sm text-gray-500">
        Лидов с такой теплотой нет
      </p>
    );
  } else {
    // KAN-961: таблица тянется до пагинации у низа экрана.
    content = (
      <FillViewportColumn data-testid="lead-temperature-column">
        <LeadTemperatureTable rows={data.results} selection={selection} />
        <PaginationBar
          page={paging.page}
          pageSize={paging.pageSize}
          count={data.count}
          hasPrevious={Boolean(data.previous)}
          hasNext={Boolean(data.next)}
          onPageChange={paging.setPage}
          onPageSizeChange={paging.setPageSize}
          className="shrink-0"
        />
      </FillViewportColumn>
    );
  }

  return (
    <PageLayout
      title="Лиды по теплоте"
      breadcrumbs={[{ label: 'Лиды по теплоте' }]}
      maxWidth="max-w-none"
      data-testid="leads-page"
    >
      <div className="min-w-0 space-y-3">
        <LeadFiltersBar filters={filters} counts={data?.counts} total={data?.total} />
        <LeadBulkBar
          count={ids.length}
          busy={bulk.isPending}
          exporting={exporter.isPending}
          onExport={() => exporter.mutate(ids)}
          onAssignManager={() => setDialog('manager')}
          onMarkTransferred={() => setDialog('transfer')}
          onUnmarkTransferred={() => bulk.mutate({ action: 'unmark_transferred', client_ids: ids })}
          onClear={selection.clear}
        />
        {content}
      </div>
      <LeadTransferDialog
        open={dialog === 'transfer'}
        count={ids.length}
        pending={bulk.isPending}
        onClose={closeDialog}
        onConfirm={(transferredTo) =>
          bulk.mutate(
            { action: 'mark_transferred', client_ids: ids, transferred_to: transferredTo },
            { onSuccess: closeDialog }
          )
        }
      />
      <LeadAssignManagerDialog
        open={dialog === 'manager'}
        count={ids.length}
        pending={bulk.isPending}
        onClose={closeDialog}
        onConfirm={(managerId) =>
          bulk.mutate(
            { action: 'assign_manager', client_ids: ids, manager_id: managerId },
            { onSuccess: closeDialog }
          )
        }
      />
    </PageLayout>
  );
}
